import type {
  HorizonType,
  Nullable,
  Property,
  QuadPoints,
  Range2D,
  ScaleType,
  UpdateValue,
} from '@library/types'
import { convertGlobalGeologicalCoordinatesToLocal, requestRedraw } from '@library/utils'
import { useEffect, useRef } from 'react'

import { HorizonRender } from '../HorizonRender'

const getCoorinates = (horizon: HorizonType, origin: Range2D): QuadPoints => [
  convertGlobalGeologicalCoordinatesToLocal(origin, horizon.header.points[0]),
  convertGlobalGeologicalCoordinatesToLocal(origin, horizon.header.points[1]),
  convertGlobalGeologicalCoordinatesToLocal(origin, horizon.header.points[2]),
  convertGlobalGeologicalCoordinatesToLocal(origin, horizon.header.points[3]),
]

export const useShader = (
  containerElement: Nullable<HTMLCanvasElement>,
  programs: Property<Record<string, WebGLProgram>>,
  scale: ScaleType,
  origin: Range2D,
  horizon: Nullable<HorizonType>,
  palette: Nullable<number[]>,
  attributeParams: [number, number],
  setUpdate: UpdateValue<number>,
) => {
  const horizonRenderRef = useRef<Nullable<HorizonRender>>(null)

  const dispose = () => {
    horizonRenderRef.current?.[Symbol.dispose]()
    horizonRenderRef.current = null
    requestRedraw(setUpdate)
  }

  useEffect(() => {
    if (horizonRenderRef.current || !containerElement || !horizon) return dispose

    horizonRenderRef.current = new HorizonRender(
      containerElement,
      programs,
      scale,
      getCoorinates(horizon, origin),
      horizon.header.nullValue,
      horizon.body,
      palette,
      attributeParams,
    )
    requestRedraw(setUpdate)

    return dispose
  }, [horizon, containerElement, setUpdate])

  useEffect(() => {
    if (!horizonRenderRef.current || !horizon) return

    horizonRenderRef.current.setCoordinates(getCoorinates(horizon, origin))
    requestRedraw(setUpdate)
  }, [horizon, origin.x, origin.y, origin.width, origin.height, setUpdate])

  useEffect(() => {
    if (!horizonRenderRef.current) return

    horizonRenderRef.current.setZoomState(scale)
    requestRedraw(setUpdate)
  }, [scale, setUpdate])

  useEffect(() => {
    if (!horizonRenderRef.current || !palette) return

    horizonRenderRef.current.setPalette(palette)
    requestRedraw(setUpdate)
  }, [palette, setUpdate])

  useEffect(() => {
    if (!horizonRenderRef.current) return

    horizonRenderRef.current.setAttributeParams(attributeParams)
    requestRedraw(setUpdate)
  }, [attributeParams, setUpdate])

  return horizonRenderRef.current
}
